import { EnvironmentVariables } from "@/modules/common/types";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";

/**
 * Direct client for workflow-manager's INTERNAL-pool controllers (/v1/cases, /v1/tasks).
 *
 * fetchFromBrandedUrl can't be reused here: passing custom headers replaces its
 * Authorization header, and workflow-manager additionally requires the tenant header.
 * The bearer token is the agent's internal-pool Cognito idToken — the same one the
 * CRM backend calls use — NOT the external customer pool (/v1/client/*).
 */
const WM_TENANT_KEY = "crm";

export const wmUrl = (path: string): string => {
  const base = EnvironmentVariables.WORKFLOW_MANAGER_BASE_API_URL as unknown as
    | string
    | undefined;
  if (!base) {
    throw new Error(
      "REACT_APP_WORKFLOW_MANAGER_BASE_API_URL is not set — add it to .env and restart the dev server."
    );
  }
  return `${base}${path}`;
};

const getInternalPoolToken = (): string | undefined => {
  const usernameKey = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
  const username = localStorage.getItem(usernameKey);
  const idTokenKey = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.idToken`;
  return localStorage.getItem(idTokenKey) ?? Cookies.get("accessToken");
};

/**
 * The full axios response, for the few callers that need more than the body — today only
 * §KYC-form's generator, which reads the server's filename off `Content-Disposition`.
 * `wmRequest` is this plus `.data`, so both share one auth header and one error translation.
 */
export async function wmRequestRaw<T>(
  config: AxiosRequestConfig & { formData?: boolean }
): Promise<AxiosResponse<T>> {
  try {
    const response = await axios({
      ...config,
      headers: {
        Authorization: `Bearer ${getInternalPoolToken()}`,
        "x-tenant-key": WM_TENANT_KEY,
        // multipart: let axios/the browser set the Content-Type boundary itself
        ...(config.formData ? {} : { "Content-Type": "application/json" }),
      },
    });
    return response as AxiosResponse<T>;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 413) {
        throw new Error(
          "The uploaded file is too large. Please try a smaller file."
        );
      }
      if (error.code === "ERR_NETWORK" && error.message === "Network Error") {
        throw new Error(
          "Request failed due to network or file size limit. Please try again."
        );
      }
      throw error;
    }
    throw new Error("An unexpected error occurred");
  }
}

/** The response BODY — what almost every caller wants. */
export async function wmRequest<T>(
  config: AxiosRequestConfig & { formData?: boolean }
): Promise<T> {
  const response = await wmRequestRaw<T>(config);
  return response.data;
}

/**
 * §Blob-errors: a `responseType: "blob"` request gets its ERROR body as a Blob too, so
 * `wmErrorMessage`'s `data.message` lookup finds nothing and the agent reads "Request failed with
 * status code 400" instead of WM's actual reason ("This step is already decided."). Read the blob
 * back into the JSON shape the translator expects; anything unparseable is left alone.
 */
export async function wmBlobError(error: unknown): Promise<unknown> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await error.response.data.text());
      error.response.data = parsed;
    } catch {
      /* not JSON (a proxy's HTML error page, say) — leave it for the generic fallback */
    }
  }
  return error;
}

/** Human message out of a workflow-manager error (Nest exceptions carry message: string | string[]).
 *  §WM-verbatim (product ask, 2026-08-06): mirror the WM FE's own client exactly —
 *  `data.message ?? error.message`, non-strings stringified — so the CRM never invents wording
 *  the WM UI wouldn't show for the same failure. */
export function wmErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message =
      (error.response?.data as { message?: unknown } | undefined)?.message ??
      error.message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string") return message;
    // §LocalizedText: the engine refuses some answers with a {en, ar} pair rather than a string
    // (payment-proof validation, for one). JSON.stringify dumped the whole object into the toast —
    // the agent saw `{"en":"Choose how the payment was made …","ar":"اختر طريقة الدفع …"}` instead
    // of a sentence. Take the locale we render the tab in; keep `ar` as the fallback so a
    // message that only ships Arabic still reads.
    if (message != null && typeof message === "object") {
      const localized = message as { en?: unknown; ar?: unknown };
      if (typeof localized.en === "string") return localized.en;
      if (typeof localized.ar === "string") return localized.ar;
      return JSON.stringify(message);
    }
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
